import {
  GridCellInstance,
  GridColumnDef,
  GridDynamicStyleOption,
  GridHeaderInstance,
  GridRawRowDataType,
  GridRowDataType,
  GridRowInstance,
  GridSettingEnvValue,
} from '@/common/components/molecules/modernGrid/modernGrid.types';
import { isEqual, isNil, isArray } from 'lodash-es';
import {
  CHECKBOX_COLUMN_ID,
  EXPAND_BTN_COLUMN_ID,
  NUMBER_COLUMN_ID,
  COLUMN_NAME_SIZE_EXACT_RULES,
  COLUMN_NAME_SIZE_CONTAINS_RULES,
  ColumnNameSizeRule,
} from './modernGrid.define';

export const getIsDefaultColumn = (columnId: string): boolean => {
  const defaultColumnIds = [NUMBER_COLUMN_ID, CHECKBOX_COLUMN_ID, EXPAND_BTN_COLUMN_ID];
  return defaultColumnIds.includes(columnId);
};

export const getComparisonData = (data: any, excludeProp: string = 'children') => {
  const excludeProps = [excludeProp, 'rowId', 'checked', 'indeterminate', 'expand', 'data'];
  const excludeObject: Record<string, true> = {};
  excludeProps.forEach((prop) => {
    excludeObject[prop] = true;
  });

  return Object.entries(data ?? {})?.reduce(
    (acc, [key, value]) => {
      if (!excludeObject[key]) {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, any>,
  );
};

export const getRowIdByData = <RowDataType extends GridRawRowDataType>(
  data: GridRawRowDataType,
  rowData: Map<string, GridRowDataType<RowDataType>> | undefined,
  excludeProp: string = 'children',
): string | undefined => {
  const rawData = getComparisonData(data, excludeProp);
  return Array.from(rowData?.entries() || []).find(([_, value]) =>
    isEqual(getComparisonData(value, excludeProp), rawData),
  )?.[0];
};

export const getRawRows = <RowDataType extends GridRawRowDataType>(
  state: true | Record<string, boolean>,
  rowsMap: Map<string, GridRowDataType<RowDataType>> | undefined,
): RowDataType[] => {
  const rawRows: RowDataType[] = [];
  Object.keys(state)?.forEach((rowId) => {
    const rowData = rowsMap?.get(rowId);
    if (state[rowId] && rowData && !rowData.isDetail) {
      const { rowId: _, ...rowDataWithoutId } = rowData;
      rawRows.push(rowDataWithoutId as unknown as RowDataType);
    }
  });
  return rawRows;
};

const findLastHeaderDepth = <RowDataType extends GridRawRowDataType>(
  header: GridHeaderInstance<RowDataType>,
  columnId: string,
): number => {
  if (!header.subHeaders || header.subHeaders.length === 0) {
    return header.depth;
  }
  const matchingSubHeader = header.subHeaders.find((subHeader) => subHeader.column.id === columnId);
  if (matchingSubHeader) {
    return findLastHeaderDepth(matchingSubHeader, columnId);
  }
  return header.depth;
};

export const getHeaderRowSpan = <RowDataType extends GridRawRowDataType>(
  header: GridHeaderInstance<RowDataType>,
): number => {
  const columnId = header.column.id;
  const currentDepth = header.headerGroup.depth;
  const lastDepth = findLastHeaderDepth(header, columnId);
  return header.column.depth === header.headerGroup.depth ? lastDepth - currentDepth : 0;
};

export const checkNumberStr = (str: string) => /^\d+(\.\d+)?$/.test(str);

export const matchArray = (cellValue: any, keyword: string) => {
  return (
    Array.isArray(cellValue) &&
    cellValue.some((item: any) => {
      const chipName = typeof item === 'object' && item?.name ? item.name : item;
      return chipName.toLowerCase().includes(keyword);
    })
  );
};

export const matchStringValue = (cellValue: any, keyword: string) => {
  return cellValue.toLowerCase().includes(keyword);
};

export const matchesCondition = <RowDataType extends GridRawRowDataType>(
  styleOption: GridDynamicStyleOption,
  instance: GridCellInstance<RowDataType> | GridRowInstance<RowDataType>,
): boolean => {
  const { columnId, type, condition } = styleOption;
  const isCellCondition = type === 'cell';
  const isCellData = 'column' in instance;
  const instanceColumnId = isCellData ? instance.column.id : '';
  const originalData = isCellData ? instance.row.original : instance.original;

  if (isCellCondition !== isCellData) {
    return false;
  }

  return isCellCondition
    ? columnId === instanceColumnId && condition(originalData[columnId], originalData)
    : condition(originalData[columnId], originalData);
};

export const deepCompare = (
  data1: any,
  data2: any,
  options: { exceptCompareKeys?: string[] } = {},
): boolean => {
  const { exceptCompareKeys = [] } = options;

  if (data1 === null || data2 === null) {
    return data1 === data2;
  }

  if (typeof data1 !== typeof data2) return false;

  if (Array.isArray(data1) && Array.isArray(data2)) {
    if (data1.length !== data2.length) return false;
    return data1.every((item, index) => deepCompare(item, data2[index], { exceptCompareKeys }));
  }

  if (
    typeof data1 === 'object' &&
    typeof data2 === 'object' &&
    !(data1 === null || data2 === null)
  ) {
    const keys1 = Object.keys(data1);
    const keys2 = Object.keys(data2);

    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => {
      if (exceptCompareKeys?.includes(key)) return true;
      return deepCompare(data1[key], data2[key], { exceptCompareKeys });
    });
  }

  if (
    (typeof data1 === 'string' || typeof data1 === 'number' || typeof data1 === 'boolean') &&
    (typeof data2 === 'string' || typeof data2 === 'number' || typeof data2 === 'boolean')
  ) {
    return data1 === data2;
  }

  return true;
};

export const existObjectValue = (data: Record<string, any> | undefined): boolean =>
  !!data && typeof data === 'object' && !!Object.keys(data)?.length;

const matchesExactRule = (lowerColumnId: string, rule: ColumnNameSizeRule): boolean =>
  rule.keywords.some((keyword) => lowerColumnId === keyword);

const matchesContainsRule = (lowerColumnId: string, rule: ColumnNameSizeRule): boolean =>
  rule.keywords.every((keyword) => lowerColumnId.includes(keyword));

export const getColumnSizeByName = (columnId: string): number | undefined => {
  const lower = columnId.toLowerCase();

  const exactMatch = COLUMN_NAME_SIZE_EXACT_RULES.find((rule) => matchesExactRule(lower, rule));
  if (exactMatch) {
    return exactMatch.size;
  }

  const containsMatch = COLUMN_NAME_SIZE_CONTAINS_RULES.find((rule) =>
    matchesContainsRule(lower, rule),
  );
  return containsMatch?.size;
};

export const isEqualArrayValue = (arr1: string[], arr2: string[]): boolean => {
  if (arr1.length !== arr2.length) return false;

  const sortedArr1 = [...arr1].sort();
  const sortedArr2 = [...arr2].sort();

  return sortedArr1.every((value, index) => value === sortedArr2[index]);
};

export const isChangedEnv = <K extends keyof GridSettingEnvValue>(
  envValue: GridSettingEnvValue[K],
  newValue: GridSettingEnvValue[K],
  isArrayType: boolean = false,
): boolean => {
  const hasChanged = envValue && !deepCompare(envValue, newValue);
  const isNilEnv = isNil(envValue);
  const isNonEmptyNewValue =
    newValue &&
    (isArrayType && isArray(newValue) ? newValue.length > 0 : existObjectValue(newValue));

  return hasChanged || (isNilEnv && isNonEmptyNewValue);
};

export const getCanFilterColumns = <RowDataType extends GridRawRowDataType>(
  columns: GridColumnDef<RowDataType>[],
): GridColumnDef<RowDataType>[] =>
  columns?.filter(({ enableGlobalFilter }) => enableGlobalFilter) || [];

/**
 * 행 구성 변경 후 Virtualizer의 전체 실측 캐시를 초기화할지 결정한다.
 * @param options 현재/이전 행 개수와 root 행 ID, 확장 상태 및 가변 detail 모드 여부
 * @returns root 구성이 같은 가변 detail 확장/축소면 false, 모드 또는 root 구성 변경이면 true
 * @remarks 가변 detail 모드가 유지되는 동안 행 개수가 같으면 기존처럼 전체 측정을 수행하지 않는다.
 */
export const shouldResetVirtualMeasurements = ({
  currentRowCount,
  previousRowCount,
  currentRootRowIds,
  previousRootRowIds,
  expandedStateChanged,
  variableHeightDetailModeChanged,
  usesVariableHeightDetailRows,
}: {
  currentRowCount: number;
  previousRowCount: number;
  currentRootRowIds: string[];
  previousRootRowIds: string[];
  expandedStateChanged: boolean;
  variableHeightDetailModeChanged: boolean;
  usesVariableHeightDetailRows: boolean;
}): boolean => {
  if (variableHeightDetailModeChanged) {
    return true;
  }
  if (currentRowCount === previousRowCount) {
    return false;
  }
  if (!usesVariableHeightDetailRows || !expandedStateChanged) {
    return true;
  }

  const hasSameRootRows =
    currentRootRowIds.length === previousRootRowIds.length &&
    currentRootRowIds.every((rowId, index) => rowId === previousRootRowIds[index]);
  return !hasSameRootRows;
};

export const getUpdatedRows = <RowDataType extends GridRawRowDataType>(
  rows: RowDataType[],
  rowIndex: number,
  columnId: string,
  value: any,
): RowDataType[] => {
  return (
    rows?.map((row, index) => {
      if (index === rowIndex) {
        return {
          ...row,
          [columnId]: value,
        };
      }
      return row;
    }) ?? []
  );
};

/**
 * get row by field in tree grid
 * @param rows tree grid rows
 * @param field row field name
 * @param value row field value
 * @returns
 */
export const getRowByField = <RowDataType extends GridRawRowDataType>(
  rows: GridRowDataType<RowDataType>[],
  field: string,
  value: unknown,
): GridRowDataType<RowDataType> | null => {
  for (let ix = 0; ix < rows.length; ix++) {
    const row = rows[ix];
    if (row[field] === value) {
      return row;
    }
    if (row.children) {
      const result = getRowByField(row.children as GridRowDataType<RowDataType>[], field, value);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

/**
 * 그리드 빈 상태(empty row) 메시지 결정
 *
 * 원본 rows가 있는데 표시할 행이 0건이면(검색/필터 결과 없음) `searchEmptyMessage`,
 * 미지정 시 `noRecordsText`(검색 결과 없음 기본 문구)를 반환한다.
 * 원본 데이터 자체가 없으면 `emptyMessage`, 미지정 시 `noDataText`를 반환한다.
 *
 * @param hasOriginRows 원본 rows 존재 여부
 * @param emptyMessage 원본 데이터가 없을 때 표시할 메시지 (미지정 시 noDataText)
 * @param searchEmptyMessage 검색/필터 결과가 0건일 때 표시할 메시지 (미지정 시 noRecordsText, ''로 억제 가능)
 * @param noDataText 데이터 없음 기본 문구 (t('MESSAGE.NO_DATA'))
 * @param noRecordsText 검색 결과 없음 기본 문구 (t('MESSAGE.NO_RECORDS'))
 * @returns 표시할 메시지
 * @example
 * resolveEmptyStateMessage({ hasOriginRows: true, emptyMessage: '', noDataText: 'No data', noRecordsText: '검색 결과가 없습니다.' });
 * // → '검색 결과가 없습니다.'
 * @remarks 검색/필터 0건 문구는 전 그리드 기본 적용이다. 원본 데이터가 있는 상태에서
 * "데이터가 없습니다"는 틀린 안내이므로 교정 목적 — 억제하려면 searchEmptyText: ''를 지정한다.
 */
export const resolveEmptyStateMessage = ({
  hasOriginRows,
  emptyMessage,
  searchEmptyMessage,
  noDataText,
  noRecordsText,
}: {
  hasOriginRows: boolean;
  noDataText: string;
  noRecordsText: string;
  emptyMessage?: string;
  searchEmptyMessage?: string;
}): string =>
  hasOriginRows ? (searchEmptyMessage ?? noRecordsText) : (emptyMessage ?? noDataText);
